import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppConfig } from '@env/app-config';
import { SUPPRESS_HTTP_ERRORS } from '@shared/auth/interceptors/error.interceptor';
import {
  ActualizarCantidadItemRequest,
  AgregarItemCarritoRequest,
  Carrito,
  FusionCarritoRequest,
  FusionResult,
  ItemAFusionar,
  ItemGuardado,
  MetodoEntrega
} from '../models/carrito.model';

/**
 * API del carrito del comprador.
 *
 * **Todas** las operaciones suprimen el aviso global de error. El backend del carrito devuelve sus
 * fallos como reglas de negocio con `code` (`OFFER_NOT_AVAILABLE`, `INSUFFICIENT_STOCK`,
 * `C2C_LIMIT_EXCEEDED`, `PRICE_CHANGED`…), que el componente traduce con `mapError` a un mensaje que
 * dice qué pasó y qué hacer. Encima de eso, el interceptor agregaba su mensaje genérico —"No
 * encontrado", "El recurso solicitado no existe"— y el comprador recibía dos avisos para un solo
 * error, el segundo sin información útil.
 *
 * La contrapartida es que **cada consumidor debe mostrar su propio mensaje de error**: acá ya no hay
 * red de seguridad. Si un `subscribe` solo apaga un spinner en el `error`, el fallo queda invisible.
 */
@Injectable({ providedIn: 'root' })
export class CarritoService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${AppConfig.apiUrl}/api/prosell/sales/carrito`;

  obtenerActivo(storefrontId?: number | null, context?: HttpContext): Observable<Carrito> {
    // 404 = comprador sin carrito activo todavía, un estado normal que el componente muestra como
    // carrito vacío.
    return this.http.get<Carrito>(this.apiUrl, this.opciones(storefrontId, context));
  }

  agregarItem(request: AgregarItemCarritoRequest, context?: HttpContext): Observable<Carrito> {
    return this.http.post<Carrito>(`${this.apiUrl}/items`, request, this.opciones(null, context));
  }

  modificarCantidad(itemId: number, request: ActualizarCantidadItemRequest, context?: HttpContext): Observable<Carrito> {
    return this.http.put<Carrito>(`${this.apiUrl}/items/${itemId}`, request, this.opciones(null, context));
  }

  eliminarItem(itemId: number, storefrontId?: number | null, context?: HttpContext): Observable<Carrito> {
    return this.http.delete<Carrito>(`${this.apiUrl}/items/${itemId}`, this.opciones(storefrontId, context));
  }

  /**
   * M02/01 RN-006 (RF-010): el comprador acepta el precio vigente de un ítem cuyo precio cambió.
   * Es la vía para desbloquear la confirmación sin perder el ítem — cambiar la cantidad no refresca
   * el precio congelado, así que sin esto la única salida sería borrar y volver a agregar.
   */
  aceptarPrecio(itemId: number, storefrontId?: number | null, context?: HttpContext): Observable<Carrito> {
    return this.http.post<Carrito>(`${this.apiUrl}/items/${itemId}/aceptar-precio`, {},
      this.opciones(storefrontId, context));
  }

  /** Método de entrega de un vendedor del carrito: envío a domicilio o recojo en tienda (solo si el vendedor lo ofrece). */
  cambiarMetodoEntrega(sellerClientId: number, deliveryMethod: MetodoEntrega, storefrontId?: number | null,
                       context?: HttpContext): Observable<Carrito> {
    return this.http.put<Carrito>(`${this.apiUrl}/vendedores/${sellerClientId}/metodo-entrega`,
      { deliveryMethod, storefrontId: storefrontId ?? null }, this.opciones(null, context));
  }

  /** M02/01 RF-014: vacía el carrito completo. Queda vacío pero abierto para seguir comprando. */
  vaciar(storefrontId?: number | null, context?: HttpContext): Observable<Carrito> {
    return this.http.delete<Carrito>(this.apiUrl, this.opciones(storefrontId, context));
  }

  /**
   * M02.2.1 RF-022: recupera un carrito que el job de abandonados marcó ABANDONED. Sin esto el
   * comprador que sigue la notificación encuentra un carrito vacío, porque ABANDONED es terminal.
   */
  reactivar(cartId: number, context?: HttpContext): Observable<Carrito> {
    return this.http.post<Carrito>(`${this.apiUrl}/${cartId}/reactivar`, {}, this.opciones(null, context));
  }

  /** M02/01 FASE 6 (RF-005): saca un ítem del carrito activo para revisar más tarde. */
  guardarParaDespues(itemId: number, storefrontId?: number | null, context?: HttpContext): Observable<Carrito> {
    return this.http.post<Carrito>(`${this.apiUrl}/items/${itemId}/guardar`, {},
      this.opciones(storefrontId, context));
  }

  listarGuardados(context?: HttpContext): Observable<ItemGuardado[]> {
    return this.http.get<ItemGuardado[]>(`${this.apiUrl}/guardados`, this.opciones(null, context));
  }

  moverAlCarrito(savedItemId: number, storefrontId?: number | null, context?: HttpContext): Observable<Carrito> {
    return this.http.post<Carrito>(`${this.apiUrl}/guardados/${savedItemId}/mover`, {},
      this.opciones(storefrontId, context));
  }

  eliminarGuardado(savedItemId: number, context?: HttpContext): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/guardados/${savedItemId}`, this.opciones(null, context));
  }

  /** M02/01 FASE 6 (RF-026): fusiona en lote el carrito de un invitado en el carrito activo. */
  fusionar(items: ItemAFusionar[], storefrontId?: number | null, context?: HttpContext): Observable<FusionResult> {
    const body: FusionCarritoRequest = { items, storefrontId: storefrontId ?? null };
    return this.http.post<FusionResult>(`${this.apiUrl}/fusionar`, body, this.opciones(null, context));
  }

  /** Contexto nuevo por petición: `HttpContext` es por request, no se comparte entre llamadas. */
  private opciones(storefrontId?: number | null, context?: HttpContext): { params: HttpParams; context: HttpContext } {
    let params = new HttpParams();
    if (storefrontId != null) {
      params = params.set('storefrontId', storefrontId.toString());
    }
    return { params, context: (context ?? new HttpContext()).set(SUPPRESS_HTTP_ERRORS, true) };
  }
}
